import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import toast from "react-hot-toast";
import { sanitizeErrorMessage } from "@/app/schedules/[tn]/ErrorMessageSanitization";
import { CommunicationSettings } from "@/clients/ntas/basic-selfcare";
import { CallForwardingVariableStatusEnum } from "@/clients/ntas/profiles";
import { NetworkProvParamsResponse } from "@/clients/spp";
import { flattenObject, getValueWithDotNotation, TN_REQUIRED_LENGTH, unflattenObject } from "@/utils/general";
import { retryLogic } from "@/utils/network";
import { NetworkProvUpdateRequestUpdated } from "@/utils/sppHelper";

export type OptionEnumType = { readonly On: "ON"; readonly Off: "OFF" };
enum specialKeys {
    simRing = "simultaneousRing",
    seqRing = "sequentialRing",
    selReject = "selectiveCallRejection",
    selAcceptance = "selectiveCallAcceptance",
    selCallForwarding = "selectiveCallForwarding",
}
const retrieveKeyConditions = (deltaForPatch: CommunicationSettings, data: { communicationSettings: CommunicationSettings } | undefined) => {
    return {
        [specialKeys.seqRing]: {
            listKey: "seqrngList",
            deltaList: deltaForPatch.sequentialRing?.seqrngList,
            dataList: data?.communicationSettings.sequentialRing?.seqrngList,
        },
        [specialKeys.simRing]: {
            listKey: "simrngList",
            deltaList: deltaForPatch.simultaneousRing?.simrngList,
            dataList: data?.communicationSettings.simultaneousRing?.simrngList,
        },
        [specialKeys.selReject]: {
            listKey: "selRejectList",
            deltaList: deltaForPatch.selectiveCallRejection?.selRejectList,
            dataList: data?.communicationSettings?.selectiveCallRejection?.selRejectList,
        },
        [specialKeys.selAcceptance]: {
            listKey: "scaList",
            deltaList: deltaForPatch.selectiveCallAcceptance?.scaList,
            dataList: data?.communicationSettings?.selectiveCallAcceptance?.scaList,
        },
        [specialKeys.selCallForwarding]: {
            listKey: "scfList",
            deltaList: deltaForPatch.selectiveCallForwarding?.scfList,
            dataList: data?.communicationSettings?.selectiveCallForwarding?.scfList,
        },
    };
};
/**
 * This function is used to get the voice settings for the given phone number.
 * @param [tn] - The phone number whose voice settings are to be fetched.
 * @return The voice settings for the given phone number.
 */
export async function getVoiceSettings(tn: string): Promise<{
    communicationSettings: CommunicationSettings;
}> {
    const res: Response = await fetch(`/api/v1/tns/${tn}/voice-settings`);
    if (!res.ok) {
        const err: string = await res.text();
        throw new Error(sanitizeErrorMessage(err), { cause: res.status });
    }
    return res.json();
}

export function useVoiceSettingsQuery(tn: string) {
    return useQuery({
        queryKey: ["voice-settings", tn],
        queryFn: () => getVoiceSettings(tn),
        retry: retryLogic,
        enabled: !!tn && tn.length === TN_REQUIRED_LENGTH, // This is to prevent the API call from being made when the TN is invalid.
        staleTime: Infinity, // This is to tell react-query to never refetch the data.
    });
}

/**
 * This function is used to patch the voice settings for the given phone number.
 * @param [tn] - The phone number whose voice settings are to be patched.
 * @param [newSettings] - The new settings to be patched.
 */
const patchVoiceSettings = async (tn: string, newSettings: { communicationSettings: CommunicationSettings }) => {
    const sanitizedSettings: { communicationSettings: CommunicationSettings } = structuredClone(newSettings);
    const selRejectList = sanitizedSettings.communicationSettings.selectiveCallRejection?.selRejectList;

    if (selRejectList) {
        sanitizedSettings.communicationSettings.selectiveCallRejection!.selRejectList = selRejectList.map(({ token: _token, ...entry }) => entry);
    }

    const res: Response = await fetch(`/api/v1/tns/${tn}/voice-settings`, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(sanitizedSettings),
    });
    if (!res.ok) {
        const err: string = await res.text();
        throw new Error(sanitizeErrorMessage(err), { cause: res.status });
    }
};

/**
 * This is a react-query hook that patches the voice settings for the given phone number.
 * @param tn - The phone number whose voice settings are to be patched.
 * @param clear - A function to clear the temp settings on a successful update.
 * @param successMessge - The message to be shown in toaster on successful update.
 */
export const usePatchVoiceSettings = (tn: string, clear: (state: object) => void, successMessage: string = "") => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ["voice-settings", tn],
        mutationFn: async (newSettings: { communicationSettings: CommunicationSettings }) => {
            await patchVoiceSettings(tn, newSettings);
        },
        onSuccess: async () => {
            clear({});
            toast.dismiss();
            toast.success(successMessage || "Saved");
            await queryClient.invalidateQueries({
                queryKey: ["voice-settings", tn],
            });
            await queryClient.invalidateQueries({ queryKey: ["schedule", tn] }); // refetch the schedules status since if you update an advanced setting should refetch the schedule and
            await queryClient.invalidateQueries({ queryKey: ["profiles", tn] });
        },
        onError: (err) => {
            toast.error("Something went wrong. Please try again later. \n" + err.message, { duration: 10000 });
        },
        retry: retryLogic,
    });
};

/**
 * This function is used to get the Call Protect value for the given phone number.
 * @param [tn] - The phone number whose voice settings are to be fetched.
 * @return The Call Protect settings for the given phone number.
 */
export async function getCallProtectSetting(tn: string): Promise<NetworkProvParamsResponse> {
    const res: Response = await fetch(`/api/v1/tns/${tn}/network-prov-settings`);
    if (!res.ok) {
        const err: string = await res.text();
        throw new Error(sanitizeErrorMessage(err), { cause: res.status });
    }
    return res.json();
}

export function useCallProtectQuery(tn: string) {
    return useQuery({
        queryKey: ["call-protect", tn],
        queryFn: () => getCallProtectSetting(tn),
        retry: retryLogic,
        enabled: !!tn && tn.length === TN_REQUIRED_LENGTH, // This is to prevent the API call from being made when the TN is invalid.
        staleTime: Infinity, // This is to tell react-query to never refetch the data.
    });
}

/**
 * This function is used to patch the Call Protect settings for the given phone number.
 * @param [tn] - The phone number whose voice settings are to be patched.
 * @param [newSettings] - The new settings to be patched.
 */
const patchCallProtectSettings = async (tn: string, newSettings: NetworkProvUpdateRequestUpdated) => {
    const res: Response = await fetch(`/api/v1/tns/${tn}/network-prov-settings`, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(newSettings),
    });
    if (!res.ok) {
        const err: string = await res.text();
        throw new Error(sanitizeErrorMessage(err), { cause: res.status });
    }
};

/**
 * This is a react-query hook that patches the Call Protect settings for the given phone number.
 * @param tn - The phone number whose Call Protect settings are to be patched.
 */
export const usePatchCallProtect = (tn: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ["call-protect", tn],
        mutationFn: async (newSettings: NetworkProvUpdateRequestUpdated) => {
            await patchCallProtectSettings(tn, newSettings);
        },
        onSuccess: async () => {
            toast.dismiss();
            toast.success("Saved");
            await queryClient.invalidateQueries({
                queryKey: ["call-protect", tn],
            });
        },
        onError: async (err) => {
            toast.error("Something went wrong. Please try again later. " + err.message, { duration: 10000 }); // need to invalidate Call Protect
            await queryClient.invalidateQueries({
                queryKey: ["call-protect", tn],
            }); // invalidate the query to show the previous state
        },
        retry: retryLogic,
    });
};

/**
 *
 * @param commSettings the commsettings to check if any callforwarding advanced settings is on
 * @param setAdvancedWarning  this is a function that sets the state of the advanced warning
 */
export function toggleWarnCallForwarding(deltaForPatch: CommunicationSettings, setAdvancedWarning: (isOn: boolean) => void) {
    if (
        _.get(deltaForPatch, "callForwardingVariable.status") === CallForwardingVariableStatusEnum.On ||
        _.get(deltaForPatch, "selectiveCallForwarding.status") === CallForwardingVariableStatusEnum.On ||
        _.get(deltaForPatch, "simultaneousRing.status") === CallForwardingVariableStatusEnum.On ||
        _.get(deltaForPatch, "sequentialRing.status") === CallForwardingVariableStatusEnum.On
    ) {
        setAdvancedWarning(true);
    } else {
        setAdvancedWarning(false);
    }
}

export const filterOriginalCommSettings = (
    deltaForPatch: CommunicationSettings,
    setDeltaForPatch: (deltaForPatch: CommunicationSettings) => void,
    data: { communicationSettings: CommunicationSettings } | undefined,
) => {
    const keyConditions = retrieveKeyConditions(deltaForPatch, data);
    if (!(Object.entries(deltaForPatch).length > 0)) return null;
    const flatDeltaForPatch = flattenObject(deltaForPatch);
    const deletedParentKeys = new Set<string>();
    const validateIfDeltaForPatchIsUnique = Object.keys(flatDeltaForPatch).reduce(
        (acc, key) => {
            // this method is to filter out the settings that are not changed if those settings not changed from the original settings then deltaforpatch should not have that key

            const originalValue = getValueWithDotNotation(data?.communicationSettings, key);
            const parentKey = key.split(".")[0];
            if (deletedParentKeys.has(parentKey)) {
                // Skip processing if the parent key has already been deleted
                return acc;
            }
            let conditionMet = false; // Flag to track if any condition in Object.entries is met

            Object.entries(keyConditions).forEach(([key, condition]) => {
                if (parentKey === key && condition.deltaList !== undefined && (condition.deltaList?.length ?? 0) !== (condition.dataList?.length ?? 0)) {
                    delete acc[parentKey]; // Delete the parent key if any field does not match the original value
                    deletedParentKeys.add(parentKey); // Add the key to a set to avoid redundant checks
                    conditionMet = true; // Set the flag to true
                }
            });
            if (!conditionMet) {
                // this checks if the original value is same as the delta value
                // TODO: Inefficient check, after deleting key, we should break out of the loop for the parent key and look for next parent key
                if (_.isEqual(flatDeltaForPatch[key], originalValue)) {
                    acc[parentKey] = deltaForPatch[parentKey as keyof CommunicationSettings]; // set the parentkey
                } else {
                    // if check the id in seqRing and anything usi ng numberlist compare that to the currentidx +1
                    if (key.endsWith(".id")) {
                        // basically what im checking is in any voice settings with numberlist we store in the deltaforpatch an ID with index of seqrnglist +1 this is not in the original settings so its ignored used for UI
                        // do nothing
                    } else {
                        delete acc[parentKey]; // delete the parent key if any field does not match the original value=
                        deletedParentKeys.add(parentKey); // add the key to a set so dont need to check isequal after. might want to optimize this later
                    }
                }
            }

            return acc;
        },
        {} as Record<string, CommunicationSettings[keyof CommunicationSettings]>,
    );

    // deltaforPatch is the object that is used to send the patch request to the voice settings API ( its also checked to determine if the save button is enabled or disabled)
    // Check if validateIfDeltaForPatchIsUnique is an empty object if its empty that means deltaforpatch settings are different from original
    if (Object.keys(validateIfDeltaForPatchIsUnique).length !== 0) {
        const changes: CommunicationSettings = unflattenObject(validateIfDeltaForPatchIsUnique);
        // setdeltaforpatch omits the whole object if all keys in object are same (deltaforpatch needs to have unique keys if they arent unique then saveChanges should be disabled)
        setDeltaForPatch(_.omit(deltaForPatch, Object.keys(changes)));
    }
};
